import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';

/**
 * useState wrapper that persists the value to localStorage under `key`.
 * Falls back to `defaultValue` when storage is empty, unparseable, or the
 * stored value has a different `typeof` than the default.
 *
 * Writes are best-effort — wrapped in try/catch because Safari private mode
 * throws on setItem.
 */
export function usePersistedState<T>(
  key: string,
  defaultValue: T,
): [T, Dispatch<SetStateAction<T>>] {
  const defaultRef = useRef(defaultValue);
  const [value, setValue] = useState<T>(() => {
    if (typeof window === 'undefined') return defaultValue;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === null) return defaultValue;
      const parsed = JSON.parse(raw) as unknown;
      if (typeof parsed !== typeof defaultValue) return defaultValue;
      return parsed as T;
    } catch {
      return defaultValue;
    }
  });

  const setAndPersist = useCallback<Dispatch<SetStateAction<T>>>(
    (next) => {
      setValue((prev) => {
        const resolved = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
        try {
          if (typeof window !== 'undefined') {
            window.localStorage.setItem(key, JSON.stringify(resolved));
          }
        } catch {
          // Safari private mode etc. — ignore
        }
        return resolved;
      });
    },
    [key],
  );

  // If the consumer changes `key` without remounting, reload from storage.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === null) return;
      const parsed = JSON.parse(raw) as unknown;
      if (typeof parsed !== typeof defaultRef.current) return;
      // Syncing React state from localStorage (an external system) when
      // `key` changes — this is the documented exception to the
      // set-state-in-effect rule.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setValue(parsed as T);
    } catch {
      // ignore
    }
  }, [key]);

  return [value, setAndPersist];
}
